import {errorMessageKey, type ApiError} from '../../api';
import {useRouter} from '../../app/router';
import {ROUTES} from '../../app/routes';
import {useI18n, type MessageKey} from '../../i18n';
import {useGuestSession, type SessionStatus} from '../../session';
import {
  ActionLink,
  BrandMark,
  Screen,
  ScreenBody,
  ScreenFooter,
  TerminalNotice,
  TopBar,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';
import './q19.css';

/**
 * # Q19 · Expired / regenerated / invalid QR
 *
 * Route `/invalid`, and the screen an unmatched URL falls to (`App.tsx`).
 *
 * ## This is a designed-for state, not a crash page
 *
 * Table QRs are regenerable if they are abused, so a guest holding a dead token
 * is a routine outcome of a working feature rather than a fault. Nothing here
 * apologises for the system and there is no diagnostic. The page says which of
 * four things happened and then gives one instruction, because a guest at a
 * table wants the next step, not the cause.
 *
 * ## Why there is no retry button
 *
 * A guest cannot retry a dead credential from inside a web page — the fix
 * happens with a camera, in another app, pointed at a piece of laminated card.
 * A button labelled "scan again" would be a control that cannot do the thing it
 * names, and on a screen whose entire purpose is to get a confused person
 * un-stuck that is worse than no button. The only action rendered here is the
 * one that genuinely works: when the session is *alive* and the URL is simply
 * unrecognised, a link back into the menu. `q19.rescan` is therefore unused —
 * see the note at the end of this comment.
 *
 * ## The dead credential is already gone
 *
 * `GuestSessionProvider` clears persisted storage on both failure paths before
 * this screen ever renders, so there is nothing to clean up here — and calling
 * `clear()` would be actively harmful, because it resets `status` to `idle` and
 * this screen would flip mid-view from "your QR expired" to "you never scanned
 * anything". The cart is left completely alone: it survives a re-scan, and
 * throwing away a guest's chosen dishes because a token rotated would be a
 * second, larger failure on top of the first.
 *
 * ## Which sentence a guest sees
 *
 * Four flavours, and they are four different problems with four different
 * remedies. The distinguishing evidence is the API error code, which the
 * session provider preserves on both the open and the refresh path:
 *
 *   `invalid_token` / `tenant_mismatch`  the server does not recognise a
 *       credential this phone was holding, or a token printed on a card this
 *       guest is looking at. That is what a regenerated table QR looks like
 *       from the guest's side — **and it is the case where a guest may be at
 *       the wrong table**, so it gets its own sentence.
 *   `token_expired` / `unauthenticated`  the credential was real and lapsed.
 *   404 / `validation_failed`            not a table QR for this venue at all.
 *   nothing scanned, at `/invalid`       the no-QR landing.
 *
 * An **unmatched URL** resolves to `invalid` rather than to the no-QR state:
 * arriving at a path this surface does not serve means something was scanned or
 * followed, so "this QR does not work" is true and "you have not scanned
 * anything" is not. `App.tsx` documents the same reasoning for routing here at
 * all, and a spine test asserts it.
 *
 * Two states beyond the spec'd four, both of which would otherwise produce a
 * confident lie:
 *
 *   `live`   an unmatched URL while the session is *working*. Telling a guest
 *            mid-meal that their QR is dead would send them to wave at staff
 *            over a bad link. They get "not found" and a way back to the menu.
 *   service  the venue's API answered with a fault, or did not answer at all.
 *            The QR may be perfectly good; blaming it would be a guess. The
 *            server's own error code picks the sentence via `errorMessageKey`.
 *
 * ## REDESIGN · the terminal-notice shell, and which ring goes with which flavour
 *
 * The design package draws exactly one end-of-road screen — the success screen —
 * and `TerminalNotice` is that shape shared out: a 56px ring in a state colour, a
 * headline at the one intermediate type step, body copy, and the ways out in a
 * footer with no rule above it. This screen used to draw its own ring, its own
 * heading size and its own action; all three are the shared ones now.
 *
 * The ring's colour is the one real decision, and it is not the same for all six
 * flavours. The screen's old stylesheet argued that **none** of them should be
 * red, because a regenerated QR is a designed-for state rather than a fault, and
 * red framing sends a guest hunting for someone to fix something that is not
 * broken. That is still right about most of these, and it is not right about a
 * dead credential: Dried Chili is the foundation's assigned tone for exactly this
 * screen, and the thing the guest is holding genuinely does not work. So:
 *
 *   * dead credential (invalid / expired / regenerated) → `error`, Dried Chili.
 *     One ring, on a page with no red fill, no alarm framing and unchanged copy
 *     that still names the remedy rather than the fault.
 *   * nothing scanned, not found, venue's API faulting → `warning`, Palm Sugar.
 *     Nothing the guest is holding is broken in any of the three, and Palm Sugar
 *     is this surface's "needs attention and is not an error".
 *
 * Never Pandan, in any branch: there is no money on this screen, and the money
 * rule (D-021) is what makes green mean one thing everywhere else.
 *
 * ## Money and imagery
 *
 * There is no money on this screen and no element that could carry Pandan. There
 * is no photograph either, and after the redesign no vector: the ring's glyph is
 * a single character in `currentColor`, so the page has no image path at all —
 * nothing to fail on 4G and nothing to fall back from.
 *
 * The one-menu 86 rule does not reach this screen — it renders no menu items,
 * and an item vanishing from the projection is Q3/Q4/Q5's visible edge. What
 * this screen owes that promise is the opposite direction: it must not eat the
 * cart, so a guest who re-scans finds their dishes still there.
 *
 * ## Reported gaps
 *
 * `q19.rescan` ("สแกนใหม่") is deliberately not rendered. It reads as a button
 * label, and no button on this surface can re-scan a QR. `q19.body` already
 * carries the instruction it would duplicate.
 *
 * The broken-QR vector this screen used to draw was better than a character at
 * saying "this code did not read" without saying "something crashed", and it is
 * gone because `TerminalNotice` takes `glyph: string`. Widening that to
 * `ReactNode` would bring it back; reported to foundations rather than forked
 * here, because a second ring on one screen is how thirteen screens end up with
 * five.
 */

type Q19Flavour =
  | 'invalid'
  | 'expired'
  | 'regenerated'
  | 'noQr'
  | 'service'
  | 'live'
  | 'opening';

/**
 * Pick the flavour from the session's own evidence.
 *
 * Pure, and ordered most-specific first: the error code is stronger evidence
 * than the coarse status, because the provider collapses several distinct codes
 * onto `expired` when a previously-working session is rejected.
 */
function flavourFor(
  status: SessionStatus,
  error: ApiError | null,
  /** Null when the URL matched no route at all. */
  pattern: string | null,
): Q19Flavour {
  if (status === 'opening') return 'opening';
  if (status === 'active') return 'live';

  const code = error?.code ?? null;
  if (code === 'invalid_token' || code === 'tenant_mismatch') return 'regenerated';
  if (code === 'token_expired' || code === 'unauthenticated') return 'expired';

  // Network, parse, 5xx, rate limits — the credential is not the accused.
  if (status === 'error') return 'service';

  if (status === 'expired') return 'expired';
  if (status === 'invalid') return 'invalid';

  // Idle, and no failure was recorded. Either the guest opened `/invalid`
  // directly with nothing scanned, or they followed a URL this surface does not
  // serve — which means they did scan or tap something.
  return pattern === null ? 'invalid' : 'noQr';
}

/**
 * What each flavour says, and how the ring is coloured while it says it.
 *
 * `tone` and `glyph` are here rather than in the JSX so that the six answers sit
 * in one table and can be read against each other — which is the only way the
 * "which of these is genuinely broken" judgment above stays reviewable.
 */
const COPY: Record<
  Q19Flavour,
  {title: MessageKey; body: MessageKey | null; tone: 'warning' | 'error'; glyph: string}
> = {
  invalid: {title: 'q19.invalidTitle', body: 'q19.body', tone: 'error', glyph: '✕'},
  expired: {title: 'q19.expiredTitle', body: 'q19.body', tone: 'error', glyph: '✕'},
  regenerated: {title: 'q19.regeneratedTitle', body: 'q19.body', tone: 'error', glyph: '✕'},
  // Nothing is broken; the guest simply has not scanned yet.
  noQr: {title: 'q19.noQrTitle', body: 'q19.noQrBody', tone: 'warning', glyph: '?'},
  // Title is replaced below by the server's own localised code.
  service: {title: 'error.generic', body: 'common.askStaff', tone: 'warning', glyph: '!'},
  live: {title: 'error.notFound', body: null, tone: 'warning', glyph: '?'},
  // Not a spinner — a spinner here would resolve into nothing. One quiet line,
  // and no ring: a state colour around a wait is a verdict nobody has reached.
  opening: {title: 'common.loading', body: null, tone: 'warning', glyph: ''},
};

export default function Q19InvalidQr() {
  const {t} = useI18n();
  const {status, error} = useGuestSession();
  const {pattern} = useRouter();

  const flavour = flavourFor(status, error, pattern);
  const copy = COPY[flavour];
  const title = flavour === 'service' ? errorMessageKey(error) : copy.title;

  return (
    <Screen className="q19-screen">
      {/*
        The language control, in the top-right corner it occupies on every other
        screen (owner directive, 2026-08-14).

        It was the **segmented** variant at the foot, and the argument written
        here for that was a real one: a guest can land on this screen first — an
        unmatched URL comes here — possibly unable to read a word of it, and
        three pills in their own script is one tap where the link variant is a
        navigation to Q2. What the corner buys instead is that the control is in
        the same place on all thirteen screens, so a guest who found it once
        never hunts for it again; and the thing Q2 opens is the same three pills,
        one tap further on, on a screen that exists for nothing else.
      */}
      <TopBar trailing={<LanguageToggle variant="link" />} />

      <ScreenBody center>
        {/*
          One live region that is always in the tree, so a flavour change is
          announced rather than replacing the node that would have announced it.
          `aria-live` rather than `role="alert"`: this is a page a guest has
          landed on, not an interruption of something they were doing, and an
          assertive alert would talk over a screen reader mid-sentence.
        */}
        <div className="q19-live" aria-live="polite">
          {flavour === 'opening' ? (
            <p className="pk-muted">{t('common.loading')}</p>
          ) : (
            <TerminalNotice tone={copy.tone} glyph={copy.glyph} title={t(title)}>
              {copy.body ? <p className="pk-muted">{t(copy.body)}</p> : null}
            </TerminalNotice>
          )}
        </div>
      </ScreenBody>

      {/* No rule above the footer: the body is a short centred message, not
          content scrolling under a bar. */}
      <ScreenFooter bordered={false} className="q19-footer">
        {/*
          The one genuinely functional action this screen ever offers, and the
          only branch where a solid red is honest — there is somewhere forward to
          go. Every other flavour's way out is a camera, so no button is drawn.
        */}
        {flavour === 'live' ? (
          <ActionLink to={ROUTES.menu} variant="primary">
            {t('common.viewMenu')}
          </ActionLink>
        ) : null}

        {/*
          The mark, not the sentence (owner directive, 2026-08-14). This screen
          is a dead end and often the *first* thing a guest ever sees — an
          unmatched URL lands here — so it is the most exposed brand surface the
          product has, and `common.attribution` in 16px hint grey was the least
          of it. Present and subordinate still: it sits under the language
          control, at the foot, at 96px. Never a lockup (D-014, foundations §7).
        */}
        <BrandMark className="q19-mark" width={96} />
      </ScreenFooter>
    </Screen>
  );
}
